import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Bot,
  Sparkles,
  Check,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { CircularProgress } from "@mui/material";
import "./agenticInsights.css";
import { fetchExplainAlert } from "./agenticInsightsApi";
import PropTypes from "prop-types";

const TASK_IDS = ["scnc", "sap", "tt", "email"];

const EMPTY_EXPANDED = TASK_IDS.reduce((acc, id) => {
  acc[id] = false;
  return acc;
}, {});

const IMPACT_STYLES = {
  positive: {
    card: "agentic-insights-impact-card--green",
    label: "agentic-insights-impact-label--green",
    icon: Check,
    iconColor: "#16a34a",
  },
  negative: {
    card: "agentic-insights-impact-card--red",
    label: "agentic-insights-impact-label--red",
    icon: AlertCircle,
    iconColor: "#dc2626",
  },
  neutral: {
    card: "agentic-insights-impact-card--amber",
    label: "agentic-insights-impact-label--amber",
    icon: AlertCircle,
    iconColor: "#d97706",
  },
};

function resolveImpactVariant(impact) {
  const key = String(impact ?? "neutral").toLowerCase();
  if (key === "positive") return "positive";
  if (key === "negative") return "negative";
  return "neutral";
}

function ImpactCard({ title, block }) {
  if (!block?.text) return null;
  const variant = resolveImpactVariant(block.impact);
  const style = IMPACT_STYLES[variant];
  const Icon = style.icon;
  return (
    <div className={`agentic-insights-impact-card ${style.card}`}>
      <div className="agentic-insights-impact-label">
        <Icon size={14} color={style.iconColor} strokeWidth={2.5} aria-hidden />
        <span className={style.label}>{title}</span>
      </div>
      <div className="agentic-insights-impact-value">{block.text}</div>
    </div>
  );
}

ImpactCard.propTypes = {
  title: PropTypes.string.isRequired,
  block: PropTypes.shape({
    text: PropTypes.string,
    impact: PropTypes.string,
  }),
};

const AgenticInsights = ({ alertDetails }) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedLogs, setExpandedLogs] = useState(EMPTY_EXPANDED);

  const alertId = alertDetails?.["Alert ID"] ?? alertDetails?.alertId ?? "";

  useEffect(() => {
    let cancelled = false;

    if (!alertId) {
      setLoading(false);
      setError("Alert ID is missing — cannot load agent analysis.");
      setInsights(null);
      return undefined;
    }

    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchExplainAlert(alertDetails);
        if (!cancelled) {
          setInsights(data);
          setExpandedLogs(EMPTY_EXPANDED);
        }
      } catch (err) {
        console.error("Failed to fetch explain-alert:", err);
        if (!cancelled) {
          setError("Failed to load AI agent analysis. Please try again.");
          setInsights(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [alertDetails, alertId]);

  const tasks = insights?.tasks ?? [];

  const toggleTask = useCallback((taskId) => {
    setExpandedLogs((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  }, []);

  const analysisItems = useMemo(() => {
    if (!insights) return [];
    const items = [];
    if (insights.supplyPipeline) {
      items.push({ label: "Supply pipeline", text: insights.supplyPipeline });
    }
    if (insights.historicalPattern) {
      items.push({ label: "Historical pattern", text: insights.historicalPattern });
    }
    if (insights.riskAssessment) {
      items.push({ label: "Risk assessment", text: insights.riskAssessment });
    }
    return items;
  }, [insights]);

  const hasAgentTasks = useMemo(
    () => tasks.some((t) => t.summary || t.logs.length > 0),
    [tasks],
  );

  if (loading) {
    return (
      <div className="agentic-insights-card agentic-insights-card--loading">
        <CircularProgress size={28} aria-label="Loading AI agent analysis" />
        <span className="agentic-insights-loading-text">Loading AI agent analysis…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agentic-insights-card agentic-insights-card--error">
        <AlertCircle size={18} aria-hidden />
        <span>{error}</span>
      </div>
    );
  }

  if (!insights) {
    return null;
  }

  return (
    <div className="agentic-insights-card">
      <div className="agentic-insights-header">
        <div className="agentic-insights-header-icon">
          <Bot size={18} aria-hidden />
        </div>
        <div className="agentic-insights-header-text">
          <div className="agentic-insights-header-title">AI Agent Analysis</div>
          <div className="agentic-insights-header-subtitle">
            Real-time supply chain intelligence
          </div>
        </div>
        <span className="agentic-insights-confidence">
          <Sparkles size={10} aria-hidden />
          Live
        </span>
      </div>

      <div className="agentic-insights-body">
        {insights.sectionSummary ? (
          <div className="agentic-insights-intro">
            <p>{insights.sectionSummary}</p>
          </div>
        ) : null}

        {/* <div className="agentic-insights-impact-row">
          <ImpactCard title="Immediate Impact" block={insights.immediateImpact} />
          <ImpactCard title="Downstream Risk" block={insights.downstreamRisk} />
        </div> */}

        {hasAgentTasks ? (
          <div className="agentic-insights-tasks">
            <div className="agentic-insights-tasks-grid">
              {tasks.map((task) => {
                if (!task.summary && task.logs.length === 0) return null;

                const isExpanded = expandedLogs[task.id];
                const previewText =
                  task.summary ||
                  (task.logs.length > 0 ? task.logs[task.logs.length - 1] : "");

                return (
                  <div key={task.id} className="agentic-insights-task">
                    <button
                      type="button"
                      onClick={() => toggleTask(task.id)}
                      className="agentic-insights-task-toggle"
                    >
                      <div className="agentic-insights-task-toggle-left">
                        <div className="agentic-insights-task-bar agentic-insights-task-bar--black" />
                        <span className="agentic-insights-task-name">{task.name}</span>
                      </div>
                      <ChevronDown
                        size={16}
                        className={`agentic-insights-chevron${isExpanded ? " agentic-insights-chevron--expanded" : ""}`}
                        strokeWidth={2}
                        aria-hidden
                      />
                    </button>

                    {!isExpanded && previewText ? (
                      <div className="agentic-insights-task-preview">
                        <div className="agentic-insights-task-preview-text">{previewText}</div>
                      </div>
                    ) : null}

                    {isExpanded && (
                      <div className="agentic-insights-task-logs">
                        {task.summary ? (
                          <div className="agentic-insights-task-summary">{task.summary}</div>
                        ) : null}
                        {task.logs.map((log) => (
                          <div key={log} className="agentic-insights-task-log">
                            <div className="agentic-insights-task-log-dot agentic-insights-task-log-dot--black" />
                            <span>{log}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {analysisItems.length > 0 ? (
          <div>
            <div className="agentic-insights-analysis-box">
              {analysisItems.map((item, idx) => (
                <div key={item.label} className="agentic-insights-analysis-item">
                  <div className="agentic-insights-analysis-num">{idx + 1}</div>
                  <div className="agentic-insights-analysis-text">
                    <strong>{item.label}:</strong> {item.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

AgenticInsights.propTypes = {
    alertDetails: PropTypes.shape({
        "Alert ID": PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        alertId: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
    }).isRequired,
};

export default AgenticInsights;
